import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { ReactNode } from "react";
import {
	LinkIcon,
	ExternalLink,
	AlertTriangle,
	ArrowRight,
	CheckCircle2,
	FileX,
	Replace,
	ShieldAlert,
	Trash2,
	XCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { withCacheBuster, openWithCacheBuster } from "./cacheBuster";
import type {
	PageIssue,
	BrokenLinkDetails,
	BrokenLinkFix,
} from "@/types/technicalAudit";

interface BrokenLinkCardProps {
	issue: PageIssue;
}

// HTTP Status badge colors based on status code
function getStatusBadgeClasses(statusCode: number): string {
	// 404, 410 - Red (Not found)
	if ([404, 410].includes(statusCode)) {
		return "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800";
	}
	// 403 - Amber (Forbidden)
	if (statusCode === 403) {
		return "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800";
	}
	// 5xx - Red (Server errors)
	if (statusCode >= 500) {
		return "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800";
	}
	// 4xx - Orange/Amber (Client errors)
	if (statusCode >= 400) {
		return "bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-800";
	}
	// Default
	return "bg-muted text-foreground border-border";
}

function getStatusLabel(statusCode: number): string {
	const labels: Record<number, string> = {
		400: "Bad Request",
		401: "Unauthorized",
		403: "Forbidden",
		404: "Not Found",
		405: "Method Not Allowed",
		408: "Request Timeout",
		410: "Gone",
		429: "Too Many Requests",
		500: "Internal Server Error",
		502: "Bad Gateway",
		503: "Service Unavailable",
		504: "Gateway Timeout",
	};
	return labels[statusCode] || `HTTP ${statusCode}`;
}

// Action badge component
function ActionBadge({ action }: { action: string }) {
	const configs: Record<string, { label: string; icon: ReactNode; className: string }> = {
		remove: {
			label: "Remove Link",
			icon: <Trash2 className="w-3 h-3" />,
			className: "bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800",
		},
		replace: {
			label: "Replace URL",
			icon: <Replace className="w-3 h-3" />,
			className: "bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800",
		},
		update: {
			label: "Update Link",
			icon: <LinkIcon className="w-3 h-3" />,
			className: "bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800",
		},
		redirect: {
			label: "Create 301 Redirect",
			icon: <ArrowRight className="w-3 h-3" />,
			className: "bg-teal-light text-teal-dark border-teal/40",
		},
	};

	const config = configs[action] || configs.update;

	return (
		<Badge variant="outline" className={cn("flex items-center gap-1.5 px-2 py-1", config.className)}>
			{config.icon}
			{config.label}
		</Badge>
	);
}

// Info Row Component
function InfoRow({
	label,
	children,
	className,
}: {
	label: string;
	children: ReactNode;
	className?: string;
}) {
	return (
		<div className={cn("space-y-1.5", className)}>
			<div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</div>
			<div className="text-sm">{children}</div>
		</div>
	);
}

// URL Display Component
function URLDisplay({
	url,
	isExternal = false,
	isError = false,
	bustCacheFor,
}: {
	url: string;
	isExternal?: boolean;
	isError?: boolean;
	/** When set, same-origin links open with a cache-buster so a stale browser/CDN
	 * copy doesn't hide a just-applied fix. Pass the client's own site URL. */
	bustCacheFor?: string;
}) {
	return (
		<a
			href={url}
			onClick={(e) => openWithCacheBuster(e, url, bustCacheFor)}
			target="_blank"
			rel="noopener noreferrer"
			title={
				bustCacheFor
					? "Opens with a cache-buster so your browser/CDN doesn't show a stale copy"
					: undefined
			}
			className={cn(
				"group flex items-start gap-2 p-3 rounded-lg border transition-all",
				"hover:shadow-sm",
				isError
					? "bg-red-50 dark:bg-red-950/40/50 border-red-200 dark:border-red-800 hover:border-red-300"
					: "bg-muted border-border hover:border-border hover:bg-muted",
			)}
		>
			<div className="flex-1 min-w-0">
				<p
					className={cn(
						"text-sm font-mono break-all",
						isError ? "text-red-700 dark:text-red-400" : "text-foreground",
					)}
				>
					{url}
				</p>
			</div>
			{isExternal && (
				<ExternalLink
					className={cn(
						"w-4 h-4 flex-shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity",
						isError ? "text-red-500 dark:text-red-400" : "text-muted-foreground",
					)}
				/>
			)}
		</a>
	);
}

function EvidenceCheck({
	label,
	value,
	met,
}: {
	label: string;
	value: ReactNode;
	met: boolean;
}) {
	return (
		<div className="flex items-start gap-3 rounded-md border bg-card p-3">
			{met ? (
				<CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
			) : (
				<XCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
			)}
			<div className="min-w-0 flex-1">
				<div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
					{label}
				</div>
				<div className="mt-1 break-words text-sm text-foreground">{value}</div>
			</div>
		</div>
	);
}

export function BrokenLinkCard({ issue }: BrokenLinkCardProps) {
	const details = issue.issue_details as BrokenLinkDetails;
	const proposedFix = issue.proposed_fix as BrokenLinkFix | null;
	const statusCode = details.status_code || 0;
	const sourceUrl = details.source_url || (details as unknown as { page_url?: string }).page_url || issue.page_url;
	const targetUrl = details.target_url || (details as unknown as { url?: string }).url || issue.page_url;

	const isCritical = [404, 410, 500, 502, 503].includes(statusCode);
	const isClientError = statusCode >= 400 && statusCode < 500;
	const isServerError = statusCode >= 500;
	const replacementUrl = proposedFix?.replacement_url;
	// A dead PAGE (its own URL 404s) is fixed with a 301 redirect, not a content
	// edit — so the "content location" gate and "Update Link" framing don't apply.
	const isRedirect = proposedFix?.action === "redirect";

	return (
		<div className="space-y-4">
			{/* Issue Alert Banner */}
			{isCritical && (
				<div
					className={cn(
						"flex items-start gap-3 rounded-xl p-4 border",
						isServerError
							? "bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800"
							: "bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800",
					)}
				>
					<div
						className={cn(
							"flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center",
							isServerError ? "bg-red-100 dark:bg-red-900/40" : "bg-amber-100 dark:bg-amber-900/40",
						)}
					>
						<FileX
							className={cn(
								"w-5 h-5",
								isServerError ? "text-red-600 dark:text-red-400" : "text-amber-600 dark:text-amber-400",
							)}
						/>
					</div>
					<div className="flex-1">
						<h4
							className={cn(
								"font-semibold",
								isServerError ? "text-red-900 dark:text-red-300" : "text-amber-900 dark:text-amber-300",
							)}
						>
							{isServerError ? "Server Error Detected" : "Broken Link Detected"}
						</h4>
						<p
							className={cn(
								"text-sm mt-1",
								isServerError ? "text-red-700 dark:text-red-400" : "text-amber-700 dark:text-amber-400",
							)}
						>
							{isServerError
								? `This link returns a ${statusCode} server error. The target server is experiencing issues.`
								: `This link returns a ${statusCode} error. Visitors clicking this link will see an error page, which hurts user experience and SEO.`}
						</p>
					</div>
					<Badge
						variant="outline"
						className={getStatusBadgeClasses(statusCode)}
					>
						{statusCode ? `${statusCode} ${getStatusLabel(statusCode)}` : "Needs Review"}
					</Badge>
				</div>
			)}

			{/* Source URL Card */}
			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center">
							<LinkIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
						</div>
						Source URL
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0">
					<InfoRow label="Found On">
						<URLDisplay url={sourceUrl} isExternal bustCacheFor={sourceUrl} />
					</InfoRow>
				</CardContent>
			</Card>

			{/* Target URL Card */}
			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div
							className={cn(
								"w-8 h-8 rounded-lg flex items-center justify-center",
								isClientError ? "bg-red-50 dark:bg-red-950/40" : isServerError ? "bg-red-50 dark:bg-red-950/40" : "bg-muted",
							)}
						>
							<AlertTriangle
								className={cn(
									"w-4 h-4",
									isClientError || isServerError ? "text-red-600 dark:text-red-400" : "text-muted-foreground",
								)}
							/>
						</div>
						Target URL
						<Badge
							variant="outline"
							className={cn("ml-auto", getStatusBadgeClasses(statusCode))}
						>
							{statusCode ? `${statusCode} ${getStatusLabel(statusCode)}` : "Needs Review"}
						</Badge>
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0 space-y-4">
					<InfoRow label="Broken URL">
						<URLDisplay url={targetUrl} isError bustCacheFor={sourceUrl} />
					</InfoRow>

					{/* Link Text */}
					{details.link_text && (
						<InfoRow label="Link Text">
							<div className="p-3 bg-muted rounded-lg border border-border">
								<p className="text-sm text-foreground italic">&ldquo;{details.link_text}&rdquo;</p>
							</div>
						</InfoRow>
					)}
				</CardContent>
			</Card>

			<Card className="border-sky-200 dark:border-sky-800 shadow-sm bg-sky-50 dark:bg-sky-950/40/30">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-sky-950 flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-sky-100 dark:bg-sky-900/40 flex items-center justify-center">
							<ShieldAlert className="w-4 h-4 text-sky-700 dark:text-sky-400" />
						</div>
						Evidence Before Fix
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0 space-y-3">
					<EvidenceCheck
						label="Source page known"
						met={Boolean(sourceUrl)}
						value={sourceUrl || "Not available"}
					/>
					<EvidenceCheck
						label="Broken target confirmed"
						met={Boolean(targetUrl)}
						value={targetUrl || "Not available"}
					/>
					<EvidenceCheck
						label="HTTP status captured"
						met={Boolean(statusCode)}
						value={statusCode ? `${statusCode} ${getStatusLabel(statusCode)}` : "Not available"}
					/>
					{isRedirect ? (
						<EvidenceCheck
							label="WordPress change"
							met={true}
							value="Creates a 301 redirect rule for the dead URL — no page content is edited, so no builder/field location is needed."
						/>
					) : (
						<EvidenceCheck
							label="Exact WordPress content location"
							met={false}
							value="Not confirmed yet. We need the exact post/page and field or builder block before a real WordPress mutation is allowed."
						/>
					)}
					<EvidenceCheck
						label="Replacement URL"
						met={Boolean(replacementUrl)}
						value={replacementUrl || "Not chosen yet. The current safe action is review or recommendation only."}
					/>
				</CardContent>
			</Card>

			{/* Proposed Fix Card */}
			{proposedFix && (
				<Card className="border-green-200 dark:border-green-800 shadow-sm bg-green-50 dark:bg-green-950/40/30">
					<CardHeader className="pb-3">
						<CardTitle className="text-sm font-semibold text-green-900 dark:text-green-300 flex items-center gap-2">
							<div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
								<Replace className="w-4 h-4 text-green-600 dark:text-green-400" />
							</div>
							Proposed Action
						</CardTitle>
					</CardHeader>
					<CardContent className="pt-0 space-y-4">
						{/* Action Type */}
						<div className="flex items-center gap-2">
							<ActionBadge action={proposedFix.action} />
						</div>

						{/* Replacement URL */}
						{proposedFix.replacement_url && (
							<InfoRow label={isRedirect ? "Redirect to" : "Replace With"}>
								<div className="flex items-center gap-3">
									<div className="flex-1">
										<URLDisplay url={proposedFix.replacement_url} isExternal bustCacheFor={sourceUrl} />
									</div>
									<ArrowRight className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
								</div>
							</InfoRow>
						)}

						{/* Reasoning */}
						{proposedFix.reasoning && (
							<InfoRow label="Reasoning">
								<div className="p-3 bg-card rounded-lg border border-green-200 dark:border-green-800">
									<p className="text-sm text-green-800 dark:text-green-300">{proposedFix.reasoning}</p>
								</div>
							</InfoRow>
						)}
					</CardContent>
				</Card>
			)}
		</div>
	);
}
